import { forwardRef } from "react";
import { onKeyDown } from "@utdanningsdirektoratet/utils/uu";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { SuggestionType } from "./types";
import styles from "./TagsInput.module.less";

type SuggestionsProps = {
  query: string;
  selectedIndex: number;
  scrollable?: number;
  suggestions?: Array<SuggestionType>;
  handleClick: (index: number) => void;
  handleHover: (index: number) => void;
  minQueryLength: number;
  isFocused: boolean;
};

const Suggestions = forwardRef<HTMLUListElement, SuggestionsProps>(
  ({ suggestions = [], scrollable, selectedIndex, query, handleClick, handleHover, minQueryLength, isFocused }, ref) => {
    const markIt = (title: string): { __html: string } => {
      const escapedRegex = query.trim().replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
      return {
        __html: title.replace(RegExp(escapedRegex, "gi"), `<span class=${styles.tagsInputSuggestionsListItemUnderlined}>$&</span>`),
      };
    };
    const listItems = suggestions.map((item, i) => {
      const listItemClasses = cls({
        [styles.tagsInputSuggestionsListItem]: true,
        [styles.tagsInputSuggestionsListItemActive]: i === selectedIndex,
      });
      const content = <span dangerouslySetInnerHTML={markIt(item.title)} />; // eslint-disable-line react/no-danger
      return (
        <li
          role="option"
          aria-selected={i === selectedIndex}
          key={i}
          onClick={() => handleClick(i)}
          onKeyDown={onKeyDown(() => handleClick(i))}
          onMouseOver={() => handleHover(i)}
          onFocus={() => handleHover(i)}
          className={listItemClasses}
        >
          {content}
        </li>
      );
    });

    if (query.length < minQueryLength || listItems.length === 0 || !isFocused) return null;

    const listClass = cls({
      [styles.tagsInputSuggestionsList]: true,
      [styles.tagsInputSuggestionsListScrollable]: scrollable && suggestions.length > scrollable,
    });

    return (
      <div className={styles.tagsInputSuggestions}>
        <ul
          ref={ref}
          role="listbox"
          style={{
            height: scrollable && suggestions.length > scrollable ? `${Math.min(scrollable, suggestions.length) * 2}rem` : "auto",
          }}
          className={listClass}
        >
          {listItems}
        </ul>
      </div>
    );
  }
);

export default Suggestions;
